import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Folder,
  HelpCircle,
  Calendar,
  TrendingUp
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const Categories = () => {
  const [categories, setCategories] = useState([])
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  
  const { getCategories, getQuestions, deleteCategory } = useFirebase()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [categoriesData, questionsData] = await Promise.all([
        getCategories(),
        getQuestions()
      ])
      setCategories(categoriesData)
      setQuestions(questionsData)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    // Check if category has questions
    const questionsInCategory = questions.filter(q => q.category === id)
    
    if (questionsInCategory.length > 0) {
      toast.error('Cannot delete category that contains questions. Please move or delete the questions first.')
      return
    }

    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory(id)
        setCategories(categories.filter(c => c.id !== id))
        toast.success('Category deleted successfully')
      } catch (error) {
        console.error('Error deleting category:', error)
        toast.error('Failed to delete category')
      }
    }
  }

  // Calculate question counts per category
  const categoriesWithCounts = categories.map(category => ({
    ...category,
    questionCount: questions.filter(q => q.category === category.id).length
  }))

  const totalQuestions = questions.length
  const emptyCategories = categoriesWithCounts.filter(c => c.questionCount === 0).length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-sm text-gray-600">
            Organize questions into categories and subjects
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link to="/categories/add" className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Add New Category
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      {categories.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="card-body text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">
                {categories.length}
              </div>
              <div className="text-sm text-gray-600">Total Categories</div>
            </div>
          </div>
          
          <div className="card">
            <div className="card-body text-center">
              <div className="text-3xl font-bold text-success-600 mb-2">
                {totalQuestions}
              </div>
              <div className="text-sm text-gray-600">Total Questions</div>
            </div>
          </div>
          
          <div className="card">
            <div className="card-body text-center">
              <div className="text-3xl font-bold text-warning-600 mb-2">
                {emptyCategories}
              </div>
              <div className="text-sm text-gray-600">Empty Categories</div>
            </div>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            All Categories ({categories.length} total)
          </h3>
        </div>
        
        <div className="card-body">
          {categoriesWithCounts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoriesWithCounts.map((category) => (
                <div key={category.id} className="card border-l-4 border-l-primary-500 hover:shadow-lg transition-shadow duration-200">
                  <div className="card-body">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900 mb-2">
                          {category.name}
                        </h4>
                        {category.description && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {category.description}
                          </p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <HelpCircle className="h-4 w-4 mr-1" />
                            {category.questionCount} questions
                          </span>
                          {category.createdAt && (
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {new Date(category.createdAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <Link
                          to={`/categories/edit/${category.id}`}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit category"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Link>
                        {category.questionCount === 0 ? (
                          <button
                            onClick={() => handleDelete(category.id)}
                            className="text-danger-600 hover:text-danger-900"
                            title="Delete category"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : (
                          <span 
                            className="text-gray-400 cursor-not-allowed" 
                            title="Cannot delete category with questions"
                          >
                            <Trash2 className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {category.questionCount > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <Link
                          to={`/questions?category=${category.id}`}
                          className="text-sm text-primary-600 hover:text-primary-800 flex items-center"
                        >
                          View questions in this category
                          <span className="ml-1">→</span>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No categories found
              </h3>
              <p className="text-gray-600 mb-4">
                Create categories to organize your exam questions.
              </p>
              <Link to="/categories/add" className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Add First Category
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Popular Categories */}
      {categoriesWithCounts.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-success-600" />
              Most Popular Categories
            </h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {categoriesWithCounts
                .filter(cat => cat.questionCount > 0)
                .sort((a, b) => b.questionCount - a.questionCount)
                .slice(0, 5)
                .map((category, index) => (
                  <div key={category.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-primary-100 text-primary-600 rounded-full text-sm font-medium">
                        {index + 1}
                      </span>
                      <div>
                        <h4 className="font-medium text-gray-900">{category.name}</h4>
                        {category.description && (
                          <p className="text-sm text-gray-600 truncate max-w-xs">
                            {category.description.substring(0, 60)}
                            {category.description.length > 60 ? '...' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">
                        {category.questionCount}
                      </div>
                      <div className="text-xs text-gray-500">questions</div>
                    </div>
                  </div>
                ))}
              
              {categoriesWithCounts.filter(cat => cat.questionCount > 0).length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No categories with questions yet. 
                  <Link to="/questions/add" className="text-primary-600 hover:text-primary-800 ml-1">
                    Add some questions
                  </Link> to get started.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Categories
